import { Globals, MainBase as MainRawBase } from "@ortho-next/three-base";
import { Vector3 } from "three";
import { CustomCamera } from "../Controls/CustomCamera.js";
import { CustomCameraControls } from "../Controls/CustomCameraControls.js";
import { MultiViewsElementCustom } from "../Controls/MultiViewsElementCustom.js";
import { ViewStateType, ViewType } from "../Models/AppModel.js";
import { bindedModelBase } from "../Models/BindedModel.js";
import { InitialFlipType } from "../Models/CaseModel.js";
import { FlipType } from "../States/State.js";
import { VectorUtils } from "../Utils/VectorUtils.js";
import { ContextInitBase } from "./ContextInit.js";
import { HistoryController } from "./HistoryController.js";
/**
 * Manages rendering and all logics.
 */
export class MainBase extends MainRawBase {
    get activeCamera() {
        return this.multiviewRenderer.activeCamera;
    }
    get activeScene() {
        return this.multiviewRenderer.activeScene;
    }
    constructor(canvasContainer, params, useOutlinePass = false) {
        super(canvasContainer, (r) => { return [new params.sceneProto(r, ViewType.AP, params.toolsAPProto, useOutlinePass, params.cameraControlsProto), new params.sceneProto(r, ViewType.LT, params.toolsLTProto, useOutlinePass, params.cameraControlsProto)]; }, { showStats: false, smartRendering: true, modelToBind: bindedModelBase, modelToMerge: params.bindedModel }, { antialias: true, preserveDrawingBuffer: true });
        this.history = new HistoryController(this);
        this._isPatientGarage = false;
        this.onBeforeRaycasting = () => {
            for (const scene of this.scenes) {
                scene.cameraControls.update();
            }
            for (const viewport of this.multiviewRenderer.viewsports) {
                viewport.cameraControls.update();
            }
        };
        this.onAfterWindowResize = () => {
            this.updateLine2Materials();
            for (const scene of this.scenes) {
                scene.cameraControls.handleResize();
            }
            for (const viewport of this.multiviewRenderer.viewsports) {
                viewport.cameraControls.handleResize();
            }
        };
        this.initViewportsAndCameraControls(params.cameraControlsProto);
        this._context = ContextInitBase.create(this, new params.contextProto());
        this.model = new params.appModel(this._context, this.history);
        this.bridge = new params.bridge(this._context, this.model);
        this.measuresController = new params.measuresController(this);
        this._saveHandler = new params.saveHandlerProto(this, this._context);
        this.printUtils = new params.printUtilsProto(this, this._context);
        window.main = this; // TODO: remove
        window.bindedModel = bindedModelBase; // TODO: remove
        this.history.setModels(bindedModelBase, this.model);
        const temp1 = new Vector3(); //todo capire
        const temp2 = new Vector3();
        this.raycasterHandler.intersectionSortComparer = (a, b) => {
            if (VectorUtils.areEqualsWithTolerance(a.distance + a.point.z, b.distance + b.point.z)) {
                const distanceToCenterA = a.point.clone().sub(a.object.getWorldPosition(temp1)).setZ(0).length();
                const distanceToCenterB = b.point.clone().sub(b.object.getWorldPosition(temp2)).setZ(0).length();
                return distanceToCenterA - distanceToCenterB;
            }
            return a.distance - b.distance;
        };
        document.addEventListener("keydown", (event) => {
            if (event.key === "Delete" && this.activeScene.activeTool) {
                this.deleteMeasurementTool();
            }
            bindedModelBase.syncAnatomicalAxis = event.ctrlKey;
        });
        document.addEventListener("keyup", (event) => {
            bindedModelBase.syncAnatomicalAxis = event.ctrlKey;
        });
        window.addEventListener("blur", () => {
            bindedModelBase.syncAnatomicalAxis = false;
        });
        for (const scene of this.scenes) {
            scene.addEventListener('select', () => {
                const view = scene.viewType;
                if (view === this.activeScene.viewType) {
                    const isSelected = view === ViewType.AP ? "isMeasurementToolSelectedAP" : "isMeasurementToolSelectedLT";
                    this.model[isSelected] = this.activeScene.activeTool.isDeletable; // isMeasurementToolSelectedAP name is fuorviante ma per ora evitiamo di rifattorizzare
                }
            });
            scene.addEventListener('deselect', () => {
                const view = scene.viewType;
                if (view === this.activeScene.viewType) {
                    const isSelected = view === ViewType.AP ? "isMeasurementToolSelectedAP" : "isMeasurementToolSelectedLT";
                    this.model[isSelected] = null;
                }
            });
            scene.addEventListener('delete', () => {
                const view = scene.viewType;
                if (view === this.activeScene.viewType) {
                    const isSelected = view === ViewType.AP ? "isMeasurementToolSelectedAP" : "isMeasurementToolSelectedLT";
                    this.model[isSelected] = null;
                }
            });
        }
    }
    /**
     * Indicates if the case has AP view.
     */
    get hasAP() {
        return !!this.caseModel.imgAP; // todo cache
    }
    /**
     * Indicates if the case has AP view.
     */
    get hasLT() {
        return !!this.caseModel.imgLT; // todo cache
    }
    /**
     * Indicates if multiple view is active.
     */
    get multiViewsActive() {
        return this.multiviewRenderer.isMultiViewsActive;
    }
    set multiViewsActive(value) {
        this.activeScene.deselectCurrentTool();
        this.multiviewRenderer.isMultiViewsActive = value;
        if (value) {
            this.model.activeViewState = ViewStateType.multiple;
        }
    }
    /**
    * Gets the camera control of the active view. It works even in multiple views.
    */
    get activeCameraControl() {
        return this.multiViewsActive ? this.multiviewRenderer.activeViewport.cameraControls : this.renderingScene.cameraControls;
    }
    /**
     * Gets all meshes of AP view.
     */
    get toolsAP() {
        return this.scenes[ViewType.AP].meshes;
    }
    /**
     * Gets all meshes of AP view.
     */
    get toolsLT() {
        return this.scenes[ViewType.LT].meshes;
    }
    /**
     * Gets all meshes of a specic view.
     */
    getTools(viewType) {
        return this.scenes[viewType].meshes;
    }
    /**
     * Gets all meshes of the active view.
     */
    get activeTools() {
        return this.activeScene.meshes;
    }
    /**
     * Set AP or Lateral rendering view.
     */
    setView(viewType) {
        this.activeScene.deselectCurrentTool();
        this.multiViewsActive = false;
        this.renderingScene = this.scenes[viewType];
        this.model.activeView = viewType;
        this.model.activeViewState = viewType; // todo, move in sceneSwitched
    }
    /**
     * Binds events to handle camera controls.
     */
    bindCameraControlsEvents() {
        for (const sceneToBind of this.scenes) {
            sceneToBind.bindEvent(["onDragStart", "onDragEnd"], () => {
                for (const scene of this.scenes) {
                    scene.cameraControls.isDragging = !scene.cameraControls.isDragging;
                }
                for (const viewport of this.multiviewRenderer.viewsports) {
                    viewport.cameraControls.isDragging = !viewport.cameraControls.isDragging;
                }
            });
        }
    }
    initViewportsAndCameraControls(cameraControlsProto = CustomCameraControls) {
        this.createViewports(cameraControlsProto);
        this.bindCameraControlsEvents();
        this.multiviewRenderer.addEventListener("multiViewsSwitched", (event) => {
            this.renderingScene.cameraControls.enabled = !event.data.value; //todo check
            this.updateLine2Materials();
            this.updateBindedZoom();
        });
        this.multiviewRenderer.addEventListener("sceneSwitched", (event) => {
            event.data.oldScene.cameraControls.enabled = false;
            event.data.newScene.cameraControls.enabled = true;
        });
        this.multiviewRenderer.addEventListener("viewportSwitched", (event) => {
            if (event.data.oldViewport) {
                event.data.oldViewport.cameraControls.enabled = false;
            }
            if (event.data.newViewport) {
                event.data.newViewport.cameraControls.enabled = true;
                this.model.activeView = event.data.newViewport.name === "APViewport" ? ViewType.AP : ViewType.LT;
            }
        });
        this.multiviewRenderer.addEventListener("cursorOnCanvas", (event) => {
            if (this.multiViewsActive) {
                this.activeCameraControl.cursorOnCanvas = event.data.value;
            }
        });
        this.syncZoom();
        this.syncPan();
        this.syncFitToPlane();
        this.activeCameraControl.enabled = true;
    }
    createViewports(cameraControlsProto) {
        for (let i = 0; i < 2; i++) {
            this.addMultiViewElements(new MultiViewsElementCustom(i, {
                camera: new CustomCamera(),
                scene: this.scenes[i],
                viewportParameters: { height: 1, width: 0.5, left: i * 0.5, top: 0 },
                backgroundColor: 0x000000,
                name: i === ViewType.AP ? "APViewport" : "LateralViewport"
            }, cameraControlsProto));
        }
    }
    updateLine2Materials() {
        for (const scene of this.scenes) {
            this.recursiveUpdateLine2Material(scene);
        }
    }
    recursiveUpdateLine2Material(obj) {
        if (obj.isLine2) {
            obj.updateResolution(Globals.canvasContainer.clientWidth * (this.multiViewsActive ? 0.5 : 1), Globals.canvasContainer.clientHeight);
        }
        for (const child of obj.children) {
            this.recursiveUpdateLine2Material(child);
        }
    }
    syncZoom() {
        for (let i = 0; i < 2; i++) {
            const activeSceneCtrl = this.scenes[i].cameraControls;
            activeSceneCtrl.addEventListener("zoom", (event) => {
                const cameraControls = this.multiviewRenderer.viewsports[i].cameraControls;
                activeSceneCtrl.enabled && cameraControls.zoomByFactor(event.args);
            });
            const activeViewportCtrl = this.multiviewRenderer.viewsports[i].cameraControls;
            activeViewportCtrl.addEventListener("zoom", (event) => {
                const cameraControls = this.scenes[i].cameraControls;
                activeViewportCtrl.enabled && cameraControls.zoomByFactor(event.args);
            });
        }
    }
    syncPan() {
        for (let i = 0; i < 2; i++) {
            const activeSceneCtrl = this.scenes[i].cameraControls;
            activeSceneCtrl.addEventListener("pan", (event) => {
                const cameraControl = this.multiviewRenderer.viewsports[i].cameraControls;
                activeSceneCtrl.enabled && cameraControl.panByVector(event.args);
            });
            const activeViewportCtrl = this.multiviewRenderer.viewsports[i].cameraControls;
            activeViewportCtrl.addEventListener("pan", (event) => {
                const cameraControls = this.scenes[i].cameraControls;
                activeViewportCtrl.enabled && cameraControls.panByVector(event.args);
            });
        }
    }
    syncFitToPlane() {
        for (let i = 0; i < 2; i++) {
            const activeSceneCtrl = this.scenes[i].cameraControls;
            activeSceneCtrl.addEventListener("fit", (event) => {
                const cameraControl = this.multiviewRenderer.viewsports[i].cameraControls;
                activeSceneCtrl.enabled && cameraControl.fitToPlane();
            });
            const activeViewportCtrl = this.multiviewRenderer.viewsports[i].cameraControls;
            activeViewportCtrl.addEventListener("fit", (event) => {
                const cameraControls = this.scenes[i].cameraControls;
                activeViewportCtrl.enabled && cameraControls.fitToPlane();
            });
        }
    }
    /**
     * Init scenes and restore old data.
     */
    async loadScenes(caseModel, toolsInitProto, layersProto) {
        if (this.caseModel)
            return;
        this.caseModel = caseModel;
        this.toolsInit = new toolsInitProto(this);
        await this.toolsInit.initPlaneAndRegisterPlane();
        this.applyInitialFlip();
        this.applyInitialScaleFactor();
        this.applyToolsData(caseModel);
        await this.applyInitialFlipGarage(); // on garage flip is executed after for the right image generation
        this.updateBrightnessContrastFromModel(caseModel); // we need to update brightness and contrast in a sync way to avoid problem with angular binding
        await this._saveHandler.restoreData(caseModel.model, caseModel.cacheModel);
        this.hasAP && this.updateScaleFactorCameraControls(ViewType.AP);
        this.hasLT && this.updateScaleFactorCameraControls(ViewType.LT);
        this.layers = new layersProto(this);
        this.layers.createAllBinding();
        this.start();
        this.bridge.dispatchEvent({ type: "onResult", args: 'restore' });
    }
    /**
     * Apply flip on load if model is null (data saved from image garage)
     */
    applyInitialFlip() {
        const caseModel = this.caseModel;
        if (this._isPatientGarage || caseModel.model || caseModel.cacheModel)
            return;
        if (this.hasAP) {
            const flipType = caseModel.flipTypeAP;
            if (flipType === InitialFlipType.horizontal || flipType === InitialFlipType.rotate180) {
                this.setFlip(ViewType.AP, FlipType.horizontal, true);
            }
            if (flipType === InitialFlipType.vertical || flipType === InitialFlipType.rotate180) {
                this.setFlip(ViewType.AP, FlipType.vertical, true);
            }
        }
        if (this.hasLT) {
            const flipType = caseModel.flipTypeLT;
            if (flipType === InitialFlipType.horizontal || flipType === InitialFlipType.rotate180) {
                this.setFlip(ViewType.LT, FlipType.horizontal, true);
            }
            if (flipType === InitialFlipType.vertical || flipType === InitialFlipType.rotate180) {
                this.setFlip(ViewType.LT, FlipType.vertical, true);
            }
        }
    }
    /**
     * Apply flip on first load only for patient garage
     */
    async applyInitialFlipGarage() {
        const caseModel = this.caseModel;
        if (!this._isPatientGarage || caseModel.model || caseModel.cacheModel)
            return;
        if (this.hasAP) {
            const plane = this.toolsAP.plane;
            const flipType = caseModel.flipTypeAP;
            if (flipType === InitialFlipType.rotate180) {
                plane.apply180Rotation();
            }
            else if (flipType === InitialFlipType.horizontal) {
                plane.rotation.z *= -1;
                await plane.applyHorizontalFlip();
            }
            else if (flipType === InitialFlipType.vertical) {
                plane.rotation.z *= -1;
                await plane.applyVerticalFlip();
            }
        }
        // LT not considered because it's never set on garage
    }
    /**
     * Apply scale factor on first load
     */
    applyInitialScaleFactor() {
        const caseModel = this.caseModel;
        if (this._isPatientGarage || caseModel.model || caseModel.cacheModel)
            return;
        if (this.hasAP && caseModel.scaleFactorAP) {
            bindedModelBase.scaleFactorAP = caseModel.scaleFactorAP;
            this.model.scaleFactorAP = caseModel.scaleFactorAP;
        }
        if (this.hasLT && caseModel.scaleFactorLT) {
            bindedModelBase.scaleFactorLT = caseModel.scaleFactorLT;
            this.model.scaleFactorLT = caseModel.scaleFactorLT;
        }
    }
    applyToolsData(caseModel) {
        if (this.caseModel.model || this.caseModel.cacheModel)
            return;
        const invertPlaneRotationAP = this.caseModel.flipTypeAP === InitialFlipType.horizontal || this.caseModel.flipTypeAP === InitialFlipType.vertical;
        const invertPlaneRotationLT = this.caseModel.flipTypeLT === InitialFlipType.horizontal || this.caseModel.flipTypeLT === InitialFlipType.vertical;
        const planeRotationAP = (caseModel.planeRotationAP ?? 0) * (invertPlaneRotationAP ? -1 : 1);
        const planeRotationLT = (caseModel.planeRotationLT ?? 0) * (invertPlaneRotationLT ? -1 : 1);
        if (this.hasAP) {
            const plane = this.toolsAP.plane;
            plane.brightness = caseModel.brightnessAP;
            plane.contrast = caseModel.contrastAP;
            plane.rotation.z = planeRotationAP;
        }
        if (this.hasLT) {
            const plane = this.toolsLT.plane;
            plane.brightness = caseModel.brightnessLT;
            plane.contrast = caseModel.contrastLT;
            plane.rotation.z = planeRotationLT;
        }
    }
    updateBrightnessContrastFromModel(caseModel) {
        this.model.brightnessAP = caseModel.brightnessAP;
        this.model.contrastAP = caseModel.contrastAP;
        this.model.brightnessLT = caseModel.brightnessLT;
        this.model.contrastLT = caseModel.contrastLT;
    }
    /**
     * Update camera controls scale factor to update zoom limits and fit to view default zoom, and apply fit to view.
     */
    updateScaleFactorCameraControls(viewType) {
        const scene = this.scenes[viewType];
        scene.cameraControls.tools = scene.meshes;
        scene.cameraControls.fitToPlane();
        const viewportCameraControls = this.multiviewRenderer.viewsports[viewType].cameraControls;
        viewportCameraControls.tools = scene.meshes;
        viewportCameraControls.fitToPlane();
    }
    updateBindedZoom() {
        if (this.multiViewsActive) {
            const viewportAP = this.multiviewRenderer.viewsports[ViewType.AP];
            const viewportLT = this.multiviewRenderer.viewsports[ViewType.LT];
            bindedModelBase.scaleHitboxAP = viewportAP.camera.zoom * viewportAP.cameraControls.viewportWidth;
            bindedModelBase.scaleHitboxLT = viewportLT.camera.zoom * viewportLT.cameraControls.viewportWidth;
        }
        else {
            bindedModelBase.scaleHitboxAP = this.scenes[ViewType.AP].camera.zoom;
            bindedModelBase.scaleHitboxLT = this.scenes[ViewType.LT].camera.zoom;
        }
    }
    setFlip(viewType, flipType, flipValue) {
        const plane = this.getTools(viewType).plane;
        if (flipType === FlipType.horizontal) {
            plane.horizontalFlip = flipValue ?? !plane.horizontalFlip;
        }
        else {
            plane.verticalFlip = flipValue ?? !plane.verticalFlip;
        }
    }
    deleteMeasurementTool() {
        const tool = this.activeScene.activeTool;
        tool.isDeletable && this.activeScene.deleteActiveTool();
    }
}
//# sourceMappingURL=Main.js.map